import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {JOB_INFORMATION} from 'src/types/crm';

import {BodySmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {ClickableIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Toast,
  toastApi,
  ToastTitle,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {useSelector, useDispatch} from 'react-redux';
import {
  selectJobInformation,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';
import {CRM_PERMISSIONS} from 'src/utils/crm';
import CollapsibleChipList from 'src/components/crm/lib/components/collapsible-chip-list/collapsible-chip-list';
import {
  getJobInformation,
  updateCrmJobInformationData,
} from 'src/action-creators/crm';
import {SkillsInformation} from 'src/components/crm/jobs/crud/job-details/skills';
import * as reduxApi from 'src/utils/redux-api-v2';

import css from './skills-tab.css';


type InlineSkillEditProps = {
  jobInfo: JOB_INFORMATION;
  skillType: 'mandatorySkills' | 'keySkills';
};

const InlineSkillEdit = ({jobInfo, skillType}: InlineSkillEditProps) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const editJobSkills = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.EDIT_JOB_SKILLS,
  );

  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [localJobInfo, setLocalJobInfo] =
    React.useState<JOB_INFORMATION | null>(null);

  const currentSkills: string[] =
    (skillType === 'mandatorySkills'
      ? jobInfo?.mandatorySkills
      : jobInfo?.keySkills) ?? [];

  const handleEdit = () => {
    setLocalJobInfo(jobInfo);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleLocalUpdate = (key: string, value: string | string[]) => {
    setLocalJobInfo((prev) => (prev ? {...prev, [key]: value} : prev));
  };

  const handleSave = async () => {
    if (!localJobInfo) {
      return;
    }
    setIsSaving(true);
    try {
      const response = await dispatch(
        reduxApi.post(
          `/api/jobs/${jobInfo.id}`,
          {...jobInfo, [skillType]: localJobInfo[skillType]},
          {},
          {apiPath: '/trmapi'},
        ),
      );
      dispatch(updateCrmJobInformationData(response as JOB_INFORMATION));
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>
            {t('Skills_Updated_Successfully', 'Skills Updated Successfully')}
          </ToastTitle>
        </Toast>,
      );
      setIsEditing(false);
    } catch (e: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {e?.response?.error?.message ||
              t('Error_Updating_Skills', 'Error Updating Skills')}
          </ToastTitle>
        </Toast>,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing && localJobInfo) {
    return (
      <div className={css.skillsContainer}>
        <SkillsInformation
          handleJobUpdate={handleLocalUpdate}
          jobInformation={localJobInfo}
          skillType={skillType}
        />
        <div className={css.editActions}>
          <Button
            onClick={handleSave}
            type="secondary"
            isLoading={isSaving}
            iconRightName="check"
            size="small"
          />
          <Button
            onClick={handleCancel}
            type="danger"
            disabled={isSaving}
            iconRightName="xmark"
            size="small"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={css.skillsContainer}>
      {currentSkills.length > 0 ? (
        <CollapsibleChipList chipTextList={currentSkills} />
      ) : (
        '-'
      )}
      {editJobSkills && (
        <ClickableIcon
          name="pen"
          size="small"
          color="clickable"
          onClick={handleEdit}
        />
      )}
    </div>
  );
};

export const SkillsTab = ({jobId}: {jobId?: string}) => {
  const {t, makeKey} = useLabelTranslation();
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (jobId) {
      dispatch(getJobInformation(jobId));
    }
  }, [jobId]);

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableName = ({data}) => (
    <BasicSingleCell className={css.cell}>
      <BodySmall color="secondary">
        {t(makeKey(data.label), data.label)}
      </BodySmall>
    </BasicSingleCell>
  );

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const Skills = ({data}) => {
    if (data.id === 'mustHaveSkills') {
      return (
        <BasicSingleCell>
          <InlineSkillEdit jobInfo={jobInfo} skillType="mandatorySkills" />
        </BasicSingleCell>
      );
    } else if (data.id === 'Goodtohave') {
      return (
        <BasicSingleCell>
          <InlineSkillEdit jobInfo={jobInfo} skillType="keySkills" />
        </BasicSingleCell>
      );
    } else if (data.id === 'jobDescriptionSkills') {
      return (
        <BasicSingleCell>
          <div className={css.skillsPadding}>
            {/* @ts-expect-error - TS18048 - 'jobInfo.skills.length' is possibly 'undefined'. */}
            {jobInfo?.skills?.length > 0 ? (
              <CollapsibleChipList chipTextList={jobInfo.skills || []} />
            ) : (
              '-'
            )}
          </div>
        </BasicSingleCell>
      );
    } else {
      return <BasicSingleCell>{''}</BasicSingleCell>;
    }
  };

  const tableEntries = [
    {
      label: t('Must_Have_Skills', 'Must Have Skills'),
      value: jobInfo,
      id: 'mustHaveSkills',
    },
    {
      label: t('Good_to_have_Skills', 'Good to have Skills'),
      value: jobInfo,
      id: 'Goodtohave',
    },
    {
      label: t(
        'Skills_extracted_from_Job_Description',
        'Skills extracted from Job Description',
      ),
      value: jobInfo,
      id: 'jobDescriptionSkills',
    },
  ].filter(Boolean);

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: Skills,
        },
      ]}
    />
  );
};
